import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnChanges,
    SimpleChanges,
    ChangeDetectionStrategy,
} from '@angular/core';
import { LabService } from '../../../../services/lab.service';
import type {
    ILabChildOrder,
    ILabOrder,
} from '../../../../interfaces/lab.interface';

import { AlertService } from 'src/lib/ariadna/alert';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { HttpNewService } from 'src/app/services/application/http-new.service';
import { TPrintResponse } from 'src/app/interfaces/lab.interface';
import {
    getNameDay,
    getStrEnd,
    getTekDay,
    getTime,
    strToDate,
} from '../../../application/global.function';
import { stat } from 'fs';

@Component({
    selector: 'app-order-details',
    templateUrl: './order-details.component.html',
    styleUrls: ['./order-details.component.scss'],
})
export class OrderDetailsComponent implements OnChanges {
    @Input() selectedOrder: ILabOrder | null = null;
    @Output() closeDetailsEvent = new EventEmitter<Event>();
    @Output() openDocReseachChildModalEvent = new EventEmitter<{
        result: ILabChildOrder['research_child_list'][0];
        event: Event;
    }>();

    loadingResearchList: boolean = false;
    isMobile: boolean = false;
    public labOrdersChild: ILabChildOrder['research_child_list'] = [];
    public loadingPdfIds: Set<number> = new Set<number>();

    constructor(
        private lab: LabService,
        private alert: AlertService,
        private http: HttpNewService
    ) {}
    private resizeHandler!: () => void;
    ngOnInit(): void {
        this.isMobile = window.innerWidth <= 767;

        this.resizeHandler = () => {
            this.isMobile = window.innerWidth <= 767;
        };

        window.addEventListener('resize', this.resizeHandler);
    }

    ngOnDestroy(): void {
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedOrder'] && changes['selectedOrder'].currentValue) {
            this.loadChildResearch(changes['selectedOrder'].currentValue);
        }
    }

    private loadChildResearch(order: ILabOrder): void {
        // console.log(order);
        if (!order || !order.research_id) return;

        this.loadingResearchList = true;
        this.lab.getLaboratoryChildResearch(order.research_id).subscribe({
            next: (res) => {
                // console.log(res);
                this.labOrdersChild = res.research_child_list;
                this.loadingResearchList = false;
            },
            error: (err) => {
                console.error('Error loading child research:', err);
                this.loadingResearchList = false;
            },
        });
    }
    expandedItemId: number | null = null;
    closingItemId: number | null = null;
    openDocReseachChildModal(
        result: ILabChildOrder['research_child_list'][0],
        event: Event
    ): void {
        event.stopPropagation();

        if (this.isMobile) {
            // В мобильной версии переключаем раскрытие
            this.expandedItemId =
                this.expandedItemId === result.research_id
                    ? null
                    : result.research_id;
        } else {
            // В десктопной версии открываем модальное окно
            this.openDocReseachChildModalEvent.emit({ result, event });
        }
    }

    isItemExpanded(researchId: number): boolean {
        return this.expandedItemId === researchId;
    }

    closeExpandedItem(): void {
        this.expandedItemId = null;
    }

    async goPdf(
        researchId: number | undefined,
        e: Event,
        ext: number,
        int: number
    ) {
        e.preventDefault();
        e.stopPropagation();
        if (!researchId) return;

        const type: string = int === 1 ? 'int' : 'ext';

        this.loadingPdfIds.add(researchId);

        try {
            // console.log('Navigator UserAgent:', navigator.userAgent);
            const res = (await lastValueFrom(
                this.http.get(
                    this.getDownloadUrlForPDF(researchId, type),
                    null,
                    false
                )
            )) as TPrintResponse;

            if (res && res.reportlist) {
                const base64 = res.reportlist[0].pdf;
                this.printNlab(base64); // Обновлённый метод
                return;
            }

            if (res) {
                const binaryString = res.response.data;
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString[i];
                }

                const file = new Blob([bytes.buffer], {
                    type: 'application/pdf',
                });
                const fileURL = URL.createObjectURL(file);

                window.location.href = fileURL;

                setTimeout(() => URL.revokeObjectURL(fileURL), 10000);

                // Для Safari на iOS используем создание ссылки
                const isSafari = /^((?!chrome|android).)*safari/i.test(
                    navigator.userAgent
                );
                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

                if (isSafari && isIOS) {
                    const link = document.createElement('a');
                    link.href = fileURL;
                    link.target = '_blank';
                    link.rel = 'noopener';
                    link.download = `research_${researchId}.pdf`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setTimeout(() => URL.revokeObjectURL(fileURL), 10000);
                }
            }
        } catch (error) {
            console.error(error);
            this.alert.error('Ошибка загрузки');
        } finally {
            this.loadingPdfIds.delete(researchId);
        }
    }

    isPdfLoading(researchId: number): boolean {
        return this.loadingPdfIds.has(researchId);
    }
    getDownloadUrlForPDF(researchId: number | undefined, type: string) {
        return `/labs/pdf/${researchId}.pdf?type=${type}`;
    }

    private printNlab(base64: string): void {
        const binaryString = atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        const file = new Blob([bytes.buffer], {
            type: 'application/pdf',
        });
        const fileURL = URL.createObjectURL(file);

        window.location.href = fileURL;

        setTimeout(() => URL.revokeObjectURL(fileURL), 10000);
    }

    getStatusClass(status: string): string {
        const normalizedStatus = status
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '');
        const statusMap: Record<string, string> = {
            выполнен: 'status-completed',
            ожидаетобработки: 'status-pending',
            отменён: 'status-cancelled',
            вработе: 'status-in-progress',
        };
        return statusMap[normalizedStatus] || 'status-default';
    }

    getCapitalizedStatus(status: string): string {
        if (!status) return '';
        return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    }
    getStatusIndicator(status: string): string {
        if (!status) return '';

        const statusIndicators: Record<string, string> = {
            выполнен: './assets/img/svg/status_ready.svg',
            'ожидает обработки': './assets/img/svg/status_inwork.svg',
            отменён: './assets/img/svg/status_aborted.svg',
            'в работе': './assets/img/svg/status_inwork.svg',
        };

        return statusIndicators[status.toLowerCase().trim()] || '';
    }

    getStatusClassIcon(status: string): string {
        if (!status || typeof status !== 'string') return '';

        const normalizedStatus = status
            .replace(/[\s\u00A0\u2000-\u200B\u2028-\u2029\u3000]/g, '')
            .toLowerCase();

        if (normalizedStatus === 'ожидаетобработки') {
            return 'icon-background__inwork';
        }
        if (normalizedStatus === 'отменён') {
            return 'icon-background__abort';
        }
        if (normalizedStatus === 'вработе') {
            return 'icon-background__inwork';
        }
        return '';
    }

    getDateOrder(date: Date | string | undefined): string {
        if (!date) return '';
        const dateObj = new Date(date);
        return dateObj.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
        });
    }

    strEnd(val: number | undefined, str: string, massStr: string[]): string {
        return getStrEnd(val, str, massStr);
    }
    closeDetails(event?: Event): void {
        this.closeDetailsEvent.emit(event);
    }

    getTekDay(dt: Date | null | undefined): string {
        if (dt) {
            return getTekDay(dt);
        } else {
            return '!';
        }
    }

    getNameDay(dt: Date): string {
        return getNameDay(dt);
    }

    getTime(dt: Date): string {
        return getTime(dt);
    }

    formatFullDate(dateString: string | undefined): string {
        if (!dateString) return '';

        // Используем функцию strToDate для преобразования строки в Date
        const date = strToDate(dateString);

        // Массив названий месяцев в родительном падеже
        const monthNames = [
            'января',
            'февраля',
            'марта',
            'апреля',
            'мая',
            'июня',
            'июля',
            'августа',
            'сентября',
            'октября',
            'ноября',
            'декабря',
        ];

        // Получаем часы и минуты
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');

        // Форматируем дату с временем
        return `${date.getDate()} ${
            monthNames[date.getMonth()]
        } ${date.getFullYear()} 
         в ${hours}:${minutes}`;
    }

    getDay(dateString: string | undefined): string {
        if (!dateString) return '';
        // Массив названий дней недели
        const weekDays = [
            'воскресенье',
            'понедельник',
            'вторник',
            'среда',
            'четверг',
            'пятница',
            'суббота',
        ];
        const date = strToDate(dateString);
        return `${weekDays[date.getDay()]}`;
    }
}
